import React from "react";
import { ViewState, StaticMap } from "react-map-gl";
import DeckGL, {
  PathLayer,
  WebMercatorViewport,
  FlyToInterpolator,
  IconLayer
} from "deck.gl";
import Layer, { LayerProps } from "@deck.gl/core/lib/layer";
import { HereMaps } from "../../../lib/HereMaps";
import { Address } from "../../../lib/model/Address";
import TransitionInterpolator from "@deck.gl/core/transitions/transition-interpolator";
import { IconLayerDatum } from "@deck.gl/layers/icon-layer/icon-layer";
import { LiveTrackingLocation } from "../../../lib/model/LiveTrackingLocation";
import * as _ from "lodash";

const here = new HereMaps();

type ViewPort = ViewState & {
  transitionDuration?: number;
  transitionInterpolator?: TransitionInterpolator;
  transitionEasing?: (t: number) => number;
};

type Props = {
  route: Address[];
  liveLocation?: LiveTrackingLocation;
};

type State = {
  viewport: ViewPort;
  layers: Layer[];
  liveTrackingLayer?: Layer;
  mapHover?: { pointer: [number, number]; content: string };
};

export class Map extends React.Component<Props, State> {
  readonly state: State = {
    viewport: {
      latitude: 53.5544582,
      longitude: 9.98629,
      zoom: 8,
      pitch: 20,
      bearing: 0
    },
    layers: []
  };
  render = () => {
    const { layers, liveTrackingLayer, viewport } = this.state;
    return (
      <DeckGL
        viewState={viewport}
        onViewStateChange={this.handleViewStateChange}
        controller={true}
        layers={liveTrackingLayer ? [...layers, liveTrackingLayer] : layers}
        width="100%"
        height={480}
      >
        <StaticMap
          width="100%"
          height={480}
          attributionControl={false}
          mapOptions={{ style: "mapbox://styles/mapbox/dark-v9" }}
          mapboxApiAccessToken="pk.eyJ1IjoibW92ZW1hdGVzIiwiYSI6ImNqZDRyMHVqajQ3N2syd281bGp6MGMweDgifQ._vjsWevieI74HlVZIThHrg"
        />
        {this.state.mapHover && (
          <div
            style={{
              position: "absolute",
              zIndex: 1,
              pointerEvents: "none",
              left: this.state.mapHover.pointer[0],
              top: this.state.mapHover.pointer[1],
              color: "#ffffff"
            }}
          >
            {this.state.mapHover.content}
          </div>
        )}
      </DeckGL>
    );
  };
  componentDidUpdate = (prevProps: Props, prevState: {}, snapshot: any) => {
    if (
      !_.isEqual(prevProps.route, this.props.route) &&
      this.props.route.length > 1
    ) {
      this.loadRoute();
    }
    if (this.props.liveLocation !== prevProps.liveLocation) {
      this.updateLiveLocationLayer();
    }
  };
  handleViewStateChange = (updates: any) => {
    this.setState({ viewport: updates.viewState });
  };
  loadRoute = async () => {
    // calculate route shape from addresses
    const routePoints = this.props.route.map(
      (a): [number, number] => [a.latitude, a.longitude]
    );
    const route = await here.calculateRouteShape(routePoints);

    // create route path layer
    const data = [
      {
        path: route,
        color: [56, 110, 232]
      }
    ];

    const routePathLayer = new PathLayer({
      id: "route-layer",
      data: data,
      pickable: true,
      getWidth: 4,
      widthMinPixels: 5,
      widthMaxPixels: 15,
      getPath: (d: any) => d.path,
      getColor: (d: any) => d.color
    } as LayerProps);

    // create marker icon layer

    const ICON_MAPPING = {
      marker: { x: 0, y: 0, width: 200, height: 200, mask: true }
    };

    const iconData: (IconLayerDatum & {
      name: string;
      address: string;
    })[] = this.props.route.map(a => ({
      name: a.companyName,
      address: `${a.street} ${a.houseNumber}, ${a.zipCode} ${a.city}`,
      position: [a.longitude, a.latitude] as [number, number]
    }));

    const iconLayer = new IconLayer({
      id: "stops-layer",
      data: iconData,
      pickable: true,
      iconAtlas: "/assets/marker_white.png",
      iconMapping: ICON_MAPPING,
      getIcon: d => "marker",
      getPosition: d => d.position!,
      getSize: d => 20,
      getColor: d => [255, 255, 255],
      onHover: ({ object, x, y }) => {
        this.setState({
          mapHover: object
            ? {
                pointer: [x, y],
                content: `${(object as any).name}\n${(object as any).address}`
              }
            : undefined
        });
      }
    });

    // calculate new viewport
    const lons = route.map(p => p[0]);
    const lats = route.map(p => p[1]);

    const corner1 = [Math.min(...lons), Math.min(...lats)];
    const corner2 = [Math.max(...lons), Math.max(...lats)];

    const temporaryViewport = new WebMercatorViewport({
      width: 876,
      height: 480
    });

    const newViewport: any = temporaryViewport.fitBounds([corner1, corner2], {
      padding: 100
    });

    const { longitude, latitude, zoom } = newViewport;

    // this is the case when start and target address are the same and the route shape contains only one point
    const safeZoom = zoom === Infinity ? 12 : zoom;

    this.setState({
      layers: [routePathLayer, iconLayer],
      viewport: {
        ...this.state.viewport,
        latitude,
        longitude,
        zoom: safeZoom,
        transitionDuration: 2300,
        transitionInterpolator: new FlyToInterpolator(),
        transitionEasing: easeInOutCubic
      }
    });
  };
  updateLiveLocationLayer = () => {
    const { liveLocation } = this.props;

    if (liveLocation) {
      const ICON_MAPPING = {
        marker: { x: 0, y: 0, width: 200, height: 200, mask: true }
      };

      const iconData: (IconLayerDatum & {
        name: string;
      })[] = [
        {
          name: `Geschwindigkeit: ${
            liveLocation.speedKMH
          }km/h Heading: ${liveLocation.heading}`,
          position: [liveLocation.lon, liveLocation.lat] as [
            number,
            number
          ]
        }
      ];

      const liveLocationLayer = new IconLayer({
        id: "live-location-layer",
        data: iconData,
        pickable: true,
        iconAtlas: "/assets/marker_white.png",
        iconMapping: ICON_MAPPING,
        getIcon: d => "marker",
        getPosition: d => d.position!,
        getSize: d => 20,
        getColor: d => [235, 3, 51],
        onHover: ({ object, x, y }) => {
          this.setState({
            mapHover: object
              ? {
                  pointer: [x, y],
                  content: (object as any).name
                }
              : undefined
          });
        }
      });
      this.setState({ liveTrackingLayer: liveLocationLayer });
    } else {
      this.setState({ liveTrackingLayer: undefined });
    }
  };
}

const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
};
